import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardActions from '@mui/material/CardActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import CardMedia from '@mui/material/CardMedia'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { useCurrentUser } from '../../shared/hooks/useCurrentUser'
import { useBannerQuery, useBannerUploadMutation, useBannerRemoveMutation } from './useBanner'
import { BANNER_CONSTANTS } from './bannerConstants'

/**
 * Port of `app/banner/banner.html` + `app/banner/bannerController.js`
 * (AngularJS `bannerController`) — the "Banner Login" screen reachable from
 * the workspace side-nav's "Ações Comerciais" submenu (`AppShell`, path
 * `banner` under `/workspace`, gated behind `allowedToManageBanner` — see
 * task 010's `AppRoutes.jsx`/`AppShell.jsx`, already wired to this route).
 *
 * UI kit mapping (MUI replaces Materialize, per `plan.md`'s "UI Kit"
 * decision, not a 1:1 visual port):
 * - `card` (form) / `card grey lighten-4` (preview) -> MUI `Card`.
 * - Materialize file input (`.file-field`) -> a hidden native `<input
 *   type="file">` triggered by a MUI `Button` (`component="label"`), closest
 *   equivalent available without a Materialize-style styled file input.
 * - The two `<div class="modal">` confirmation dialogs (upload / remove) ->
 *   MUI `Dialog`.
 * - `Materialize.toast(...)` -> MUI `Snackbar` + `Alert`, same pattern
 *   already used by `SettingsPage` (task 013) for the same legacy toast
 *   idiom — no new toast infra is introduced here.
 *
 * Permission check: the legacy controller read
 * `AuthService.getUser().then(user => user.allowedToManageBanner)` itself
 * and used it to block `preUploadBanner`/`executeRemoveBanner` with a toast.
 * The side-nav link is already gated behind the same flag one level up
 * (`AppShell`/`useCurrentUser`), but a user could still reach `/workspace/
 * banner` by typing the URL directly, so the same in-page guard is ported
 * here via `useCurrentUser()` (task 010's hook, which already exposes
 * `allowedToManageBanner` — no need to re-fetch the user document a second
 * time for this screen).
 */

function notifySeverity(type) {
  if (type === 'error') return 'error'
  if (type === 'warning') return 'warning'
  return 'success'
}

function isValidUrl(url) {
  if (!url || url.includes('docs.google.com/forms')) return true
  try {
    return Boolean(new URL(url.includes('://') ? url : `https://${url}`))
  } catch {
    return false
  }
}

function BannerPage() {
  const { allowedToManageBanner } = useCurrentUser()
  const { env, envLabel, currentInfo, currentBannerUrl, isLoading } = useBannerQuery()
  const uploadMutation = useBannerUploadMutation()
  const removeMutation = useBannerRemoveMutation()

  const [bannerInfo, setBannerInfo] = useState({ callToAction: '', campaign: '' })
  const [lastSeenInfo, setLastSeenInfo] = useState(null)
  const [file, setFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [openUploadModal, setOpenUploadModal] = useState(false)
  const [openRemoveModal, setOpenRemoveModal] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' })

  // Equivalent of legacy `initView()`'s `angular.extend($scope.bannerInfo,
  // {...res.data})`: seed the editable form fields from the fetched
  // `currentInfo` once it arrives, without re-clobbering user edits on every
  // re-render — same "adjust state during render" pattern used by
  // `SettingsPage` (task 013) for its `urlAstrea` override.
  if (currentInfo && currentInfo !== lastSeenInfo) {
    setLastSeenInfo(currentInfo)
    setBannerInfo({ callToAction: currentInfo.callToAction || '', campaign: currentInfo.campaign || '' })
  }

  const uploading = uploadMutation.isPending || removeMutation.isPending

  function notify(message, type = '') {
    setSnackbar({ open: true, severity: notifySeverity(type), message })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  function handleFileChange(event) {
    const selected = event.target.files && event.target.files[0]
    event.target.value = ''

    if (!selected) {
      setFile(null)
      setImagePreview(null)
      return
    }

    if (selected.type !== BANNER_CONSTANTS.ALLOWED_MIMETYPE) {
      notify('Apenas arquivos .webp são permitidos.', 'error')
      setFile(null)
      setImagePreview(null)
      return
    }

    if (selected.size > BANNER_CONSTANTS.MAX_FILE_SIZE) {
      notify('O tamanho máximo permitido é 3MB.', 'error')
      setFile(null)
      setImagePreview(null)
      return
    }

    setFile(selected)
    const reader = new FileReader()
    reader.onload = (event_) => setImagePreview(event_.target.result)
    reader.readAsDataURL(selected)
  }

  function handlePreUpload(event) {
    event.preventDefault()
    if (!allowedToManageBanner) return notify('Sem permissão para esta ação.', 'error')
    if (!file) return notify('Selecione um arquivo .webp.', 'warning')
    if (!isValidUrl(bannerInfo.callToAction)) return notify('URL de CTA inválida.', 'warning')

    setOpenUploadModal(true)
  }

  function handleConfirmUpload() {
    setOpenUploadModal(false)
    executeUpload()
  }

  function executeUpload() {
    if (!allowedToManageBanner) return notify('Sem permissão para esta ação.', 'error')

    uploadMutation.mutate(
      { file, callToAction: bannerInfo.callToAction, campaign: bannerInfo.campaign, env },
      {
        onSuccess: () => {
          notify('Banner enviado com sucesso!')
          setFile(null)
          setImagePreview(null)
        },
        onError: (error) => notify(error?.response?.data?.error || 'Erro inesperado.', 'error'),
      },
    )
  }

  function handleRemoveBanner() {
    setOpenRemoveModal(true)
  }

  function handleConfirmRemove() {
    setOpenRemoveModal(false)
    executeRemove()
  }

  function executeRemove() {
    if (!allowedToManageBanner) return notify('Sem permissão para esta ação.', 'error')

    removeMutation.mutate(
      { env },
      {
        onSuccess: () => notify('Banner removido com sucesso!'),
        onError: () => notify('Erro ao remover banner.', 'error'),
      },
    )
  }

  function closeModals() {
    setOpenUploadModal(false)
    setOpenRemoveModal(false)
  }

  return (
    <Box sx={{ maxWidth: 960 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Banner Login - Astrea
      </Typography>

      <Grid container spacing={3} sx={{ alignItems: 'flex-start' }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card component="form" variant="outlined" onSubmit={handlePreUpload}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Configurações do Banner
              </Typography>

              <Stack spacing={3} sx={{ mt: 2 }}>
                <Button component="label" variant="contained" startIcon={<UploadFileIcon />} sx={{ alignSelf: 'flex-start' }}>
                  Selecionar Imagem (.webp)
                  <input type="file" accept="image/webp,.webp" hidden onChange={handleFileChange} />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {file ? file.name : 'Upload do banner .webp. Tamanho Máx. 3Mb'}
                </Typography>

                <TextField
                  id="cta_link"
                  label="Link de Redirecionamento (CTA)"
                  fullWidth
                  value={bannerInfo.callToAction}
                  onChange={(event) => setBannerInfo((current) => ({ ...current, callToAction: event.target.value }))}
                />

                <TextField
                  id="campaign"
                  label="Nome da Campanha (Apenas Título)"
                  fullWidth
                  value={bannerInfo.campaign}
                  onChange={(event) => setBannerInfo((current) => ({ ...current, campaign: event.target.value }))}
                />
              </Stack>
            </CardContent>

            <CardActions sx={{ px: 2, pb: 2 }}>
              <Button type="submit" variant="contained" disabled={uploading}>
                Salvar Alterações
              </Button>
              <Button
                color="error"
                variant="outlined"
                type="button"
                onClick={handleRemoveBanner}
                disabled={uploading || !currentInfo?.lastModified}
              >
                Remover Banner Ativo
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined" sx={{ backgroundColor: 'grey.100' }}>
            <CardContent>
              <Typography>
                <b>Banner Ativo no Ambiente:</b>
              </Typography>
              <CardMedia
                component="img"
                src={currentBannerUrl}
                onError={(event) => {
                  event.currentTarget.src = 'https://placehold.co/400x300?text=Sem+Banner+Ativo'
                }}
                sx={{ maxHeight: 200, my: 1.5, borderRadius: 1, objectFit: 'contain' }}
              />

              <Box sx={{ mt: 2 }}>
                <Typography>
                  <strong>Ambiente Detectado:</strong> <b>{envLabel}</b>
                </Typography>
                <Typography>
                  <strong>Campanha:</strong> {currentInfo?.campaign || 'Não definido'}
                </Typography>
                <Typography>
                  <strong>Último upload:</strong>{' '}
                  {currentInfo?.lastModified ? new Date(currentInfo.lastModified).toLocaleString('pt-BR') : 'N/A'}
                </Typography>
              </Box>

              {imagePreview && (
                <Box sx={{ mt: 2.5, pt: 1.5, borderTop: '2px dashed', borderColor: 'primary.main' }}>
                  <Typography color="primary">
                    <b>Novo Preview Selecionado:</b>
                  </Typography>
                  <Box component="img" src={imagePreview} sx={{ maxHeight: 150, maxWidth: '100%', mt: 1 }} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openUploadModal} onClose={closeModals}>
        <DialogTitle>Confirmar Atualização</DialogTitle>
        <DialogContent>
          <DialogContentText>Você deseja publicar este novo banner?</DialogContentText>
          <Paper variant="outlined" sx={{ p: 1.5, mt: 2, backgroundColor: 'warning.lighter', borderColor: 'warning.main' }}>
            <Typography variant="body2">
              <b>Ambiente de Destino:</b> <Box component="span" sx={{ color: 'error.main' }}><b>{envLabel}</b></Box>
            </Typography>
          </Paper>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <b>Campanha:</b> {bannerInfo.campaign || 'Sem título'}
          </Typography>
          {imagePreview && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <i>Uma nova imagem .webp será enviada ao S3.</i>
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModals}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmUpload}>
            Confirmar e Publicar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openRemoveModal} onClose={closeModals}>
        <DialogTitle>Remover Banner Ativo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja realmente remover o banner e limpar as configurações de campanha?
          </DialogContentText>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Ambiente: <b>{envLabel}</b>
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            <b>Atenção:</b> Esta ação apagará os arquivos do S3 imediatamente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModals}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemove}>
            Confirmar Remoção
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={BANNER_CONSTANTS.TOAST_AUTO_HIDE_DURATION}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isLoading && (
        <Typography variant="caption" color="text.secondary">
          Carregando informações do banner...
        </Typography>
      )}
    </Box>
  )
}

export default BannerPage
